import {createSelector} from '@reduxjs/toolkit'
import {StoreStateType} from 'modules'
import {pathOr} from 'ramda'
import {RootState} from 'store'
import {parseUrlParams, urlParams} from 'utils/urlParams'

import {DevicePlatform} from '@vio/core/src/_web/utils/userAgent/types'
import {defaultCountry} from '@vio/core/src/localization/business/countries'
import {
  defaultLanguage,
  getTextDirection
} from '@vio/core/src/localization/services/locale'
import {safeDecode} from '@vio/core/src/utils/url'
import {DeviceCategory} from '@vio/core/src/utils/userAgent/types'

export const getQueryString = (state: RootState) => state.meta.queryString ?? ''

export const getUrlParams = createSelector([getQueryString], queryString =>
  parseUrlParams(queryString)
)

export const getUserCountry = createSelector(
  [(state: RootState) => state.meta.userCountry],
  userSelectedCountry => {
    const currentUrlParams = urlParams()
    const {userCountry} = currentUrlParams

    return userSelectedCountry || userCountry
  }
)

export const getIsIsraeliResident = createSelector(
  [getUserCountry],
  userCountry => userCountry === 'IL'
)

export const getUserCountryWithFallback = createSelector(
  [getUserCountry],
  userCountry => userCountry || defaultCountry.code
)

export const getUserCity = createSelector(
  [(state: RootState) => state.meta.userCity],
  userCity => (userCity ? safeDecode(userCity) : undefined)
)

export const getAnonymousId = (state: RootState) => state.meta.anonymousId || ''

/**
 * [PICC] The issuing country detected from the card, if any. It is only ever set
 * when the display currency is chargeable and the card's currency differs from
 * it (see `useCardCountryProbe`), so `Boolean(piccCardCountry)` doubles as "we
 * have a currency choice worth showing".
 */
export const getPiccCardCountry = (state: RootState) =>
  state.meta.piccCardCountry

// Unlike `getUserCurrencyWithFallback`, no URL fallback: unset means the user
// has not picked a currency on this page.
export const getUserSelectedCurrency = (state: RootState) => state.meta.currency

export const getIsSyntheticTest = (state: RootState): boolean =>
  state.meta.anonymousId?.startsWith('test_website_synthetics') ?? false

export const getSearchId = createSelector(
  (state: StoreStateType) => pathOr(null, ['meta', 'searchId'], state),
  searchId => searchId || null
)

export const getUserCurrencyWithFallback = createSelector(
  [(state: RootState) => state.meta.currency],
  userSelectedCurrency => {
    const currentUrlParams = urlParams()
    const {currency: urlCurrency} = currentUrlParams

    // userSelectedCurrency refers to the currency that the user might change on the payment page.
    // If it's not set, fallback to the currency from the URL parameters.
    return userSelectedCurrency || urlCurrency
  }
)

export const getLocale = createSelector(
  [(state: RootState) => state.meta.locale],
  locale => locale || defaultLanguage
)

/**
 * The text direction the emotion cache is built with in `AppProviders`. Anything
 * positioned from JavaScript has to read it from here, or it mirrors while the
 * stylesheet does not.
 */
export const getIsRtlLanguage = createSelector(
  [getLocale],
  locale => getTextDirection(locale).isRtl
)

export const getDeviceType = (state: RootState): DeviceCategory =>
  state.meta.deviceType || 'desktop'

export const getIsMobileDevice = createSelector(
  [getDeviceType],
  deviceType => deviceType !== 'desktop'
)

export const getDevicePlatform = (state: RootState): DevicePlatform =>
  state.meta.devicePlatform || 'unknown'

export const getIsVioFlutterApp = (state: RootState): boolean =>
  state.meta.isVioFlutter

export const getUserAgent = (state: RootState): string | undefined =>
  state.meta.userAgent
